/* eslint-disable react-hooks/rules-of-hooks */
import { HTTP_STATUS } from "@api/constant";
import ArticleCard from "@blocks/Card/ArticleCard";
import { RaSectionName } from "@config/SectionConstants";
import MainBlock from "@container/MainBlock";
import MainContainer, { AdsSettings } from "@container/MainContainer";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import { RenderImu1AdInfiniteScroll } from "@elements/Advertisement/RenderImu1AdInfiniteScroll";
import fetchTMApi from "@helper/fetchTMApi";
import { QN_ENTRY_RECENT } from "@helper/getEnvVariables";
import { GADataProps, getGTMData } from "@helper/getGtmData";
import { displayAfter, hideAfter } from "@helper/utils";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import {
  CreateLoadMoreHandler,
  type CreateLoadMoreHandlerProps,
} from "@hooks/useLoadMore/CreateLoadMoreHandler";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@hooks/useLoadMore/SetFirstRenderedArticleIds";
import type { Route } from "@route-types/RecentArticles/+types/RecentArticles";
import ErrorPage from "@routes/ErrorPage";
import NotFound from "@routes/NotFound";
import { excludedSections } from "@routes/RecentArticles/RecentArticlesConstants";
import type {
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { createMetaTags, MetaTagsProps } from "@sphtech/web2-seo-schema";
import { type ProcessedArticleData } from "@transformer/useOsResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import { RecentArticlesPayloadResponseType } from "@typings/PagePayloadType";
import { Fragment, useState } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import { fetchRecentArticlesData } from "./RecentArticles.server";

const PAGE_SIZE = 5;
const INITIAL_ARTICLE_COUNT = 5;

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    RecentArticlesPayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function RecentArticles() {
  const dataLoaderResponse: TRouteWithoutRedirect<
    RecentArticlesPayloadResponseType,
    string
  > = useLoaderData();

  // Check if there is any other response than success.
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }
  const recentArticles = dataLoaderResponse.payload.data;

  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);

  const showLoadMore: boolean = recentArticles.length == INITIAL_ARTICLE_COUNT;
  useSetFirstRenderedArticleIds(recentArticles, setRenderedArticleIds);
  const adsSettings: AdsSettings = useGlobalAdSetting();

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length == 0 ? recentArticles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      sort: lastLoadedArticle && lastLoadedArticle.sort,
    };

    const fetchMoreStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      try {
        const queryParams = new URLSearchParams({
          sections: excludedSections.join(","),
          sort: Array.isArray(requestData.sort)
            ? requestData.sort.join(",")
            : "",
          size: "5",
        }).toString();
        const response: ProcessedArticleData[] = await fetchTMApi(
          `more-excluded-sections-stories?${queryParams}`,
          "GET",
        );

        return response;
      } catch {
        return [];
      }
    };

    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchMoreStoriesData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: PAGE_SIZE,
      pageNumber,
    };
    const response = await CreateLoadMoreHandler(createLoadMoreProps);
    return response;
  };

  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  return (
    <MainContainer
      sectionName={RaSectionName}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adsSettings}
    >
      <WrapperBlock className="flex flex-col lg:flex-row justify-between">
        <MainBlock sectionName={RaSectionName}>
          {[...recentArticles, ...loadMoreData].map((section, index) => (
            <Fragment key={section.id}>
              <ArticleCard
                displayWidth={260}
                displayHeight={192}
                elements={{
                  hasDate: true,
                  hasQuickNews: QN_ENTRY_RECENT === "1",
                  hasShowAuthor: true,
                }}
                {...section}
                overrideStyle={{
                  cardWrapper:
                    (displayAfter(index + 1, PAGE_SIZE)
                      ? "border-none"
                      : "flex-col lg:flex-row") +
                    (hideAfter(index, PAGE_SIZE)
                      ? " flex-col lg:flex-row flex-row-reverse"
                      : ""),
                  titleStyle: hideAfter(index, PAGE_SIZE)
                    ? "font-semibold text-2sm text-gray-100 hover:text-gray-100 lg:text-2lg lg:text-red-100 lg:hover:text-red-200"
                    : "text-md lg:text-2lg",
                  paragraphStyle: hideAfter(index, PAGE_SIZE)
                    ? "hidden lg:block"
                    : "",
                  imageWrapperStyle: hideAfter(index, PAGE_SIZE)
                    ? "w-31.5 h-23 lg:h-auto"
                    : "",
                  imageStyle: hideAfter(index, PAGE_SIZE)
                    ? "h-23 lg:h-auto"
                    : "",
                }}
                refMedium={"recent_articles"}
              />
              {displayAfter(index + 1, PAGE_SIZE) && (
                <>
                  {RenderImu1AdInfiniteScroll(
                    (index + 1) / PAGE_SIZE,
                    RaSectionName,
                    pageAdTargetValue,
                  )}
                </>
              )}
            </Fragment>
          ))}
          {showLoadMore ? (
            <div className="flex w-full justify-center mb-xl">
              <LoadMore
                rootClassName="my-4 w-full"
                onLoadMore={handleLoadMore}
                hasMore={hasMoreStories}
                loadText="Load More"
                mobileInfiniteScroll={true}
                pageNumber={pageNumber}
                sectionName={RaSectionName}
              />
            </div>
          ) : null}
        </MainBlock>
        <SideBlock
          sectionName={RaSectionName}
          className="w-full lg:block"
          pageAdTargetValue={pageAdTargetValue}
          hideAdForMobile={true}
        />
      </WrapperBlock>
    </MainContainer>
  );
}

export async function loader() {
  const data = (await fetchRecentArticlesData()) as TRouteWithRedirect<
    RecentArticlesPayloadResponseType,
    string
  >;
  if (data.statusCode !== HTTP_STATUS.SUCCESS) return { ...data };

  const gaData = data.payload.gaData;

  return {
    ...data,
    gtm: getGTMData(gaData as GADataProps),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<RecentArticlesPayloadResponseType, string>
  >;
}
